/**
 * <ai-quantity-input> hydrates the authored quantity controls.
 * Host value/min/max/step drive the input; an absent max means no upper bound.
 * Changes bubble as quantity:change { quantity, min, max }.
 */
if (!customElements.get("ai-quantity-input")) {
  customElements.define(
    "ai-quantity-input",
    class QuantityInput extends HTMLElement {
      connectedCallback() {
        this._input = this.querySelector("[data-product-quantity-input]");
        if (!this._input) return;
        this._minusBtn = this.querySelector('[data-product-quantity-action="decrease"]');
        this._plusBtn = this.querySelector('[data-product-quantity-action="increase"]');
        this._boundDecrease ||= () => this._stepBy(-1);
        this._boundIncrease ||= () => this._stepBy(1);
        this._boundInputChange ||= () => this._refresh(true, this._input.value);
        this._minusBtn?.addEventListener("click", this._boundDecrease);
        this._plusBtn?.addEventListener("click", this._boundIncrease);
        this._input.addEventListener("change", this._boundInputChange);
        this._input.addEventListener("blur", this._boundInputChange);
        this._refresh(false);
      }

      disconnectedCallback() {
        this._minusBtn?.removeEventListener("click", this._boundDecrease);
        this._plusBtn?.removeEventListener("click", this._boundIncrease);
        this._input?.removeEventListener("change", this._boundInputChange);
        this._input?.removeEventListener("blur", this._boundInputChange);
        this._input = null;
      }

      static get observedAttributes() {
        return ["value", "min", "max", "step"];
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue || !this._input || this._reflecting || this._refreshQueued) return;
        // A variant updates several rule attributes in one turn. Normalize
        // against the complete rule, not each intermediate combination.
        this._refreshQueued = true;
        queueMicrotask(() => {
          this._refreshQueued = false;
          if (this.isConnected && this._input) this._refresh(true);
        });
      }

      _integerAttribute(name, fallback, minimum) {
        const raw = this.getAttribute(name);
        const value = raw == null || raw.trim() === "" ? NaN : Number(raw);
        return Number.isSafeInteger(value) && value >= minimum ? value : fallback;
      }

      _normalize(value) {
        const number = Number(value);
        const integer = Number.isSafeInteger(Math.floor(number)) ? Math.floor(number) : this._min;
        const bounded = this._max == null ? integer : Math.min(integer, this._max);
        return this._min + Math.floor((Math.max(this._min, bounded) - this._min) / this._step) * this._step;
      }

      _refresh(notify, value = this.getAttribute("value") ?? this._input.value) {
        const previous = this._value;
        this._min = this._integerAttribute("min", 1, 1);
        this._max = this._integerAttribute("max", null, 0);
        this._step = this._integerAttribute("step", 1, 1);
        this._value = this._normalize(value);
        this._input.min = String(this._min);
        this._input.step = String(this._step);
        if (this._max == null) this._input.removeAttribute("max");
        else this._input.max = String(this._max);
        this._input.value = String(this._value);
        this._reflecting = true;
        this.setAttribute("value", String(this._value));
        this._reflecting = false;
        this._updateState();
        if (notify && previous !== this._value) {
          this.dispatchEvent(new CustomEvent("quantity:change", {
            bubbles: true,
            detail: { quantity: this._value, min: this._min, max: this._max },
          }));
        }
      }

      _stepBy(direction) {
        this._refresh(true, this._input.value);
        const next = this._value + direction * this._step;
        if (next < this._min || (this._max != null && next > this._max)) return;
        this._refresh(true, next);
      }

      _updateState() {
        const unavailable = this._max != null && this._max < this._min;
        const states = [
          [this._minusBtn, unavailable || this._value <= this._min],
          [this._plusBtn, unavailable || (this._max != null && this._value + this._step > this._max)],
        ];
        for (const [button, disabled] of states) {
          if (!button) continue;
          button.disabled = disabled;
          button.setAttribute("aria-disabled", String(disabled));
        }
      }
    },
  );
}
